import {
  createAbortError,
  DETAIL_SCOPE,
  isCanceled,
  isProviderSummary,
} from './mediaDetailsState';
import getResourceFromMedia3p, {
  formatVideoLength,
} from '../../utils/getResourceFromMedia3p';

const validUrl = (url) => {
  try {
    return ['http:', 'https:'].includes(new URL(url).protocol);
  } catch {
    return false;
  }
};
const validUrls = (urls) => (urls || []).filter((url) => validUrl(url?.url));
const keyOf = (resource) => `${resource.mediaProviderId}:${resource.id}`;
// Gallery tiles only render these types; anything else would crash the pane.
const tileTypes = new Set(['image', 'gif', 'video']);
const terminalCodes = new Set([
  'Studio:InvalidProviderMediaId',
  'Studio:ProviderMediaNotFound',
  'Studio:ProviderMediaUnavailable',
  'Studio:ProviderDetailsUnsupported',
]);

export function mergeMediaDetails(summary, detail) {
  if (
    detail.providerMediaId !== summary.providerMediaId ||
    detail.name !== summary.name ||
    detail.provider?.toLowerCase() !== summary.provider?.toLowerCase() ||
    detail.detailsRequired ||
    detail.type?.toLowerCase() !== 'video'
  ) {
    throw Object.assign(new Error('Video unavailable'), {
      code: 'Studio:ProviderMediaUnavailable',
    });
  }
  const videoUrls = validUrls(detail.videoUrls);
  if (!videoUrls.some((url) => url.type === 'full'))
    throw Object.assign(new Error('Video unavailable'), {
      code: 'Studio:ProviderMediaUnavailable',
    });
  const imageUrls = validUrls(detail.imageUrls);
  return {
    ...summary,
    ...detail,
    title: detail.title || summary.title,
    createTime:
      detail.createTime && !detail.createTime.startsWith('0001-')
        ? detail.createTime
        : summary.createTime,
    updateTime:
      detail.updateTime && !detail.updateTime.startsWith('0001-')
        ? detail.updateTime
        : summary.updateTime,
    description: detail.description || summary.description,
    // Match the summary tile, which shows the first valid image as its poster.
    imageUrls: imageUrls.length ? imageUrls : validUrls(summary.imageUrls),
    videoMetadata:
      parseFloat(detail.videoMetadata?.duration) > 0
        ? detail.videoMetadata
        : summary.videoMetadata,
    videoUrls: videoUrls.map((url) => {
      const prior = summary.videoUrls?.find(
        (candidate) => candidate.type === url.type
      );
      return {
        ...url,
        width: url.width > 0 ? url.width : prior?.width || 0,
        height: url.height > 0 ? url.height : prior?.height || 0,
      };
    }),
  };
}

// One resolver per editor API context; Studio remounts the editor when the
// story, tenant or auth context changes. Nothing is persisted.
export default function createMediaDetailsResolver(
  fetchDetail,
  now = Date.now
) {
  const entries = new Map();
  const queue = [];
  let active = 0;
  let generation = 0;
  let scopeToken = {};
  let library;
  // Provider throttling outlives library and query changes.
  let cooldownUntil = 0;
  const reset = () => {
    generation++;
    scopeToken = {};
    entries.forEach((entry) => {
      entry.job?.controller.abort();
      entry.job?.reject(createAbortError());
    });
    entries.clear();
  };
  // Query changes within a library keep resolved details and pending insertions.
  const setScope = (next) => {
    if (library !== next) {
      library = next;
      reset();
    }
  };
  const isCurrent = (resource) => resource?.[DETAIL_SCOPE] === scopeToken;

  const register = (providerId, raw, index) => {
    const scorePlayVideo =
      raw?.provider?.toLowerCase() === 'scoreplay' &&
      raw?.type?.toLowerCase() === 'video';
    if (!scorePlayVideo) {
      try {
        return getResourceFromMedia3p(providerId, raw);
      } catch {
        /* Isolate malformed entries. */
      }
    }
    const identity =
      raw?.providerMediaId ||
      /^media\/SCOREPLAY:(\d+)$/i.exec(raw?.name || '')?.[1];
    const validIdentity =
      scorePlayVideo &&
      /^[1-9]\d*$/.test(identity || '') &&
      typeof identity === 'string' &&
      (identity.length < 19 ||
        (identity.length === 19 && identity <= '9223372036854775807'));
    const summary = { ...raw, providerMediaId: identity };
    const type = raw?.type?.toLowerCase();
    const resource = {
      id: raw?.name || `unavailable:${index}`,
      mediaProviderId: providerId,
      galleryOnly: true,
      [DETAIL_SCOPE]: scopeToken,
      type: tileTypes.has(type) ? type : 'video',
      mimeType: scorePlayVideo ? 'video/mp4' : undefined,
      creationDate: raw?.createTime,
      updateTime: raw?.updateTime,
      length: parseFloat(raw?.videoMetadata?.duration) || 0,
      lengthFormatted: formatVideoLength(
        parseFloat(raw?.videoMetadata?.duration) || 0
      ),
      isExternal: true,
      title: raw?.title || raw?.description || 'Unavailable media',
      alt: raw?.title || 'Unavailable media',
      poster: raw?.imageUrls?.find((url) => validUrl(url?.url))?.url,
      src: '',
      sizes: {},
      width: 0,
      height: 0,
    };
    const key = keyOf(resource);
    const state = { status: validIdentity ? 'summary' : 'unavailable' };
    const existing = entries.get(key);
    if (!existing) entries.set(key, { summary, resource, state });
    // A fresh listing makes a settled failure retryable; cached details and
    // in-flight requests are kept.
    else if (!existing.job && existing.state.status !== 'ready') {
      existing.summary = summary;
      existing.state = state;
    }
    return resource;
  };

  const startJob = (job) => {
    active++;
    const { entry, version, controller } = job;
    Promise.resolve()
      .then(() =>
        fetchDetail(
          entry.resource.mediaProviderId,
          entry.summary.providerMediaId,
          controller.signal
        )
      )
      .then((detail) => {
        if (
          version !== generation ||
          entry.job !== job ||
          controller.signal.aborted
        )
          throw createAbortError();
        const resource = getResourceFromMedia3p(
          entry.resource.mediaProviderId,
          mergeMediaDetails(entry.summary, detail)
        );
        resource[DETAIL_SCOPE] = scopeToken;
        entry.ready = resource;
        entry.expires = now() + 60000;
        entry.state = { status: 'ready' };
        job.resolve(resource);
      })
      .catch((error) => {
        if (version === generation && entry.job === job) {
          if (isCanceled(error) || controller.signal.aborted)
            entry.state = { status: 'summary' };
          else {
            const code = error?.response?.data?.error?.code || error?.code;
            const seconds = Number(
              error?.response?.data?.error?.data?.retryAfterSeconds ??
                error?.response?.headers?.['retry-after']
            );
            if (
              code === 'Studio:ProviderRateLimited' ||
              error?.response?.status === 429
            )
              cooldownUntil =
                now() +
                (Number.isFinite(seconds)
                  ? Math.min(3600, Math.max(1, seconds))
                  : 30) *
                  1000;
            entry.state = {
              status:
                terminalCodes.has(code) || error?.response?.status === 404
                  ? 'unavailable'
                  : 'error',
              code,
              retryable: code === 'Studio:ProviderMediaUnavailable',
            };
          }
        }
        job.reject(error);
      })
      .finally(() => {
        if (entry.job === job) entry.job = null;
        active--;
        pump();
      });
  };

  const pump = () => {
    while (active < 2 && queue.length) {
      const job = queue.shift();
      if (job.controller.signal.aborted) {
        job.reject(createAbortError());
        continue;
      }
      if (cooldownUntil > now()) {
        job.entry.job = null;
        job.entry.state = {
          status: 'error',
          code: 'Studio:ProviderRateLimited',
        };
        job.reject(new Error('Please wait before retrying'));
        continue;
      }
      startJob(job);
    }
  };

  const resolve = (resource, { signal } = {}) => {
    if (signal?.aborted) return Promise.reject(createAbortError());
    const entry = entries.get(keyOf(resource));
    if (!entry)
      return isProviderSummary(resource)
        ? Promise.reject(createAbortError())
        : Promise.resolve(resource);
    if (!isCurrent(resource)) return Promise.reject(createAbortError());
    if (entry.state.status === 'unavailable' && !entry.state.retryable)
      return Promise.reject(new Error('Video unavailable'));
    // Details already resolved stay usable while new requests are throttled.
    if (entry.ready && entry.expires > now())
      return Promise.resolve(entry.ready);
    if (cooldownUntil > now())
      return Promise.reject(new Error('Please wait before retrying'));
    // An abandoned request cannot accept new consumers.
    if (entry.job?.controller.signal.aborted) entry.job = null;
    if (!entry.job) {
      entry.ready = null;
      entry.expires = 0;
      const job = {
        entry,
        version: generation,
        controller: new AbortController(),
        consumers: new Set(),
      };
      job.promise = new Promise((resolveJob, reject) => {
        job.resolve = resolveJob;
        job.reject = reject;
      });
      entry.job = job;
      entry.state = { status: 'loading' };
      queue.push(job);
    }
    const job = entry.job;
    const consumer = {};
    job.consumers.add(consumer);
    const result = new Promise((accept, reject) => {
      const release = () => {
        signal?.removeEventListener('abort', cancel);
        job.consumers.delete(consumer);
      };
      const cancel = () => {
        release();
        if (!job.consumers.size) {
          job.controller.abort();
          if (job.version === generation && entry.job === job)
            entry.state = { status: 'summary' };
        }
        reject(createAbortError());
      };
      signal?.addEventListener('abort', cancel, { once: true });
      job.promise.then(
        (value) => {
          release();
          if (job.version !== generation || signal?.aborted)
            reject(createAbortError());
          else accept(value);
        },
        (error) => {
          release();
          reject(error);
        }
      );
    });
    pump();
    return result;
  };
  return {
    register,
    resolve,
    reset,
    setScope,
    isCurrent,
    // Inspection for tests; UI state lives in the consuming hooks.
    snapshot: (resource) => entries.get(keyOf(resource))?.state,
  };
}
